import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    Grid,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Email as EmailIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    GroupWork as ManageIcon,
    ExpandMore as ExpandMoreIcon,
    Download as DownloadIcon,
    Clear as ClearIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import PageHeader from '../../components/common/PageHeader';
import userListingService from '../../services/userListingService';

// Configurer dayjs pour le français
dayjs.locale('fr');

/**
 * Page principale de gestion des relations User ↔ Listings
 */
const UserListingsPage = () => {
    const navigate = useNavigate();
    
    // États
    const [userListings, setUserListings] = useState([]);  // Initialisé comme un tableau vide
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [filterBy, setFilterBy] = useState('');
    const [filterValue, setFilterValue] = useState('');
    
    // États pour la recherche avancée
    const [searchMode, setSearchMode] = useState('simple'); // 'simple' ou 'advanced'
    const [advancedSearch, setAdvancedSearch] = useState({
        userName: '',
        userEmail: '',
        listingTitle: '',
        listingStatus: '',
        dateFrom: null,
        dateTo: null
    });
    
    // États pour les actions groupées
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedAction, setSelectedAction] = useState('');
    const [processingAction, setProcessingAction] = useState(false);
    const [actionSuccess, setActionSuccess] = useState(null);
    const [actionError, setActionError] = useState(null);
    
    // États pour les modales
    const [statusChangeModal, setStatusChangeModal] = useState(false);
    const [emailModal, setEmailModal] = useState(false);
    const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
    const [statusToSet, setStatusToSet] = useState('active');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailMessage, setEmailMessage] = useState('');
    const [emailType, setEmailType] = useState('custom');

    /**
     * Charger les données des relations User ↔ Listings
     */
    const loadUserListings = async () => {
        try {
            setLoading(true);
            setError(null);
            
            let response;
            
            if (searchMode === 'advanced') {
                // Mode recherche avancée
                const searchParams = {
                    ...advancedSearch,
                    dateFrom: advancedSearch.dateFrom ? advancedSearch.dateFrom.format('YYYY-MM-DD') : null,
                    dateTo: advancedSearch.dateTo ? advancedSearch.dateTo.format('YYYY-MM-DD') : null,
                    page: page + 1,
                    limit: rowsPerPage
                };
                
                // Filtrer les paramètres vides
                const filteredParams = Object.fromEntries(
                    Object.entries(searchParams).filter(([_, value]) => value !== null && value !== '')
                );

                // Vérifier si tous les critères de recherche sont vides, si oui utiliser getUserListings à la place
                const hasSearchCriteria = Object.keys(filteredParams).some(key => 
                    !['page', 'limit'].includes(key) && filteredParams[key] !== undefined && filteredParams[key] !== null && filteredParams[key] !== ''
                );
                
                if (hasSearchCriteria) {
                    response = await userListingService.searchUserListings(filteredParams);
                } else {
                    // Si aucun critère spécifié, utilisez la méthode simple pour éviter l'erreur
                    const params = {
                        page: page + 1,
                        limit: rowsPerPage,
                        sortBy,
                        sortOrder
                    };
                    response = await userListingService.getUserListings(params);
                }
            } else {
                // Mode simple (existant)
                const params = {
                    page: page + 1,
                    limit: rowsPerPage,
                    sortBy,
                    sortOrder,
                    // Filtres spécifiques avancés
                    ...(filterBy && filterValue && { filterBy, filterValue })
                };
                
                response = await userListingService.getUserListings(params);
            }
            
            // Adapter la structure de données du backend
            let listings = [], pagination = {};
            
            if (searchMode === 'advanced') {
                // Structure de réponse différente pour searchUserListings
                listings = Array.isArray(response.data) ? response.data : 
                           Array.isArray(response) ? response : [];
                pagination = response.pagination || {};
            } else {
                // Structure normale pour getUserListings
                listings = Array.isArray(response.data?.userListings) ? response.data.userListings : [];
                pagination = response.data?.pagination || {};
            }
            
            // S'assurer que userListings est toujours un tableau
            setUserListings(Array.isArray(listings) ? listings : []);
            setTotalCount(pagination.totalItems || 0);
        } catch (err) {
            console.error('Erreur lors du chargement des relations:', err);
            
            // Gestion spécifique des erreurs d'authentification
            if (err.response?.status === 401 || err.response?.status === 403) {
                setError('Session expirée. Veuillez vous reconnecter.');
                // Optionnel : rediriger vers la page de connexion
                // navigate('/login');
            } else if (err.response?.status === 500) {
                setError('Erreur serveur. Veuillez réessayer plus tard.');
            } else {
                setError('Erreur lors du chargement des données');
            }
        } finally {
            setLoading(false);
        }
    };

    /**
     * Charger les données au montage et lors des changements
     */
    useEffect(() => {
        loadUserListings();
    }, [page, rowsPerPage, sortBy, sortOrder, filterBy, filterValue, searchMode]);

    /**
     * Gérer la sélection d'éléments
     */
    const handleSelectItem = (event, itemId) => {
        event.stopPropagation(); // Éviter la propagation de l'événement
        
        // Vérifier si l'élément est déjà sélectionné
        const isItemSelected = selectedItems.includes(itemId);
        
        if (event.target.checked && !isItemSelected) {
            // Ajouter seulement s'il n'est pas déjà dans la liste
            setSelectedItems(prev => [...prev, itemId]);
        } else if (!event.target.checked && isItemSelected) {
            // Retirer seulement s'il est dans la liste
            setSelectedItems(prev => prev.filter(id => id !== itemId));
        }
    };

    /**
     * Gérer la sélection de tous les éléments de la page
     */
    const handleSelectAllItems = (event) => {
        if (event.target.checked) {
            // Vérifier que userListings est bien un tableau avant d'utiliser map
            if (Array.isArray(userListings)) {
                // Sélectionner en utilisant directement les IDs des listings
                const newSelected = userListings.map(item => item.id);
                setSelectedItems(newSelected);
            } else {
                // Si userListings n'est pas un tableau, initialiser avec un tableau vide
                setSelectedItems([]);
            }
        } else {
            setSelectedItems([]);
        }
    };

    /**
     * Vérifier si un item est sélectionné
     */
    const isSelected = (itemId) => selectedItems.indexOf(itemId) !== -1;

    /**
     * Préparer et afficher la modale appropriée selon l'action sélectionnée
     */
    const handleBulkAction = () => {
        if (!selectedAction || selectedItems.length === 0) {
            setActionError("Veuillez sélectionner une action et au moins un élément");
            return;
        }

        // Déterminer quelle modale afficher selon l'action sélectionnée
        switch (selectedAction) {
            case 'status_change':
                setStatusChangeModal(true);
                break;
            case 'send_email':
                setEmailModal(true);
                break;
            case 'delete':
                setConfirmDeleteModal(true);
                break;
            default:
                setActionError("Action non supportée");
                break;
        }
    };

    /**
     * Extraire les IDs des éléments sélectionnés
     */
    const extractSelectedIds = () => {
        const userIds = [];
        const listingIds = selectedItems; // selectedItems contient déjà les IDs des listings

        // Récupérer les userIds correspondants
        selectedItems.forEach(listingId => {
            const listing = userListings.find(item => item.id === listingId);
            if (listing && listing.owner?.id && !userIds.includes(listing.owner.id)) {
                userIds.push(listing.owner.id);
            }
        });

        return { userIds, listingIds };
    };

    /**
     * Exécuter l'action de changement de statut
     */
    const executeStatusChange = async () => {
        try {
            setProcessingAction(true);
            setActionError(null);
            setActionSuccess(null);

            const { userIds, listingIds } = extractSelectedIds();

            // Créer l'objet d'action groupée
            const bulkAction = {
                userIds,
                listingIds,
                action: 'status_change',
                params: {
                    status: statusToSet
                }
            };

            const response = await userListingService.performBulkAction(bulkAction);

            if (response.success) {
                setActionSuccess(`Statut changé avec succès pour ${response.updatedCount || selectedItems.length} élément(s)`);
                // Recharger les données
                loadUserListings();
                // Réinitialiser la sélection
                setSelectedItems([]);
                setSelectedAction('');
                setStatusChangeModal(false);
            } else {
                setActionError(response.message || "Erreur lors du changement de statut");
            }
        } catch (err) {
            console.error('Erreur lors du changement de statut:', err);
            setActionError("Une erreur est survenue lors du changement de statut");
        } finally {
            setProcessingAction(false);
        }
    };

    /**
     * Exécuter l'action d'envoi d'email
     */
    const executeSendEmail = async () => {
        try {
            setProcessingAction(true);
            setActionError(null);
            setActionSuccess(null);

            const { userIds, listingIds } = extractSelectedIds();

            // Vérifier qu'on a bien des annonces sélectionnées
            if (listingIds.length === 0) {
                setActionError('Veuillez sélectionner au moins une annonce pour envoyer un email');
                setProcessingAction(false);
                return;
            }

            // Utiliser directement la nouvelle API bulk pour l'envoi d'email
            const bulkAction = {
                userIds,
                listingIds,
                action: 'send_email',
                params: {
                    subject: emailSubject,
                    message: emailMessage,
                    emailType: emailType
                }
            };

            const response = await userListingService.performBulkAction(bulkAction);
            if (response.success) {
                setActionSuccess(`Email envoyé avec succès à ${response.details?.emailsSent || response.updatedCount} utilisateur(s)`);
                // Réinitialiser la sélection
                setSelectedItems([]);
                setSelectedAction('');
                setEmailModal(false);
                setEmailSubject('');
                setEmailMessage('');
                setEmailType('custom');
            } else {
                setActionError(response.message || 'Erreur lors de l\'envoi des emails');
            }
        } catch (err) {
            console.error('Erreur lors de l\'envoi d\'emails:', err);
            setActionError(err.response?.data?.message || "Une erreur est survenue lors de l'envoi des emails");
        } finally {
            setProcessingAction(false);
        }
    };

    /**
     * Exécuter l'action de suppression
     */
    const executeDelete = async () => {
        try {
            setProcessingAction(true);
            setActionError(null);
            setActionSuccess(null);

            const { userIds, listingIds } = extractSelectedIds();

            // Créer l'objet d'action groupée
            const bulkAction = {
                userIds,
                listingIds,
                action: 'delete',
                params: {}
            };

            const response = await userListingService.performBulkAction(bulkAction);

            if (response.success) {
                setActionSuccess(`${response.updatedCount || selectedItems.length} élément(s) supprimé(s) avec succès`);
                // Recharger les données
                loadUserListings();
                // Réinitialiser la sélection
                setSelectedItems([]);
                setSelectedAction('');
                setConfirmDeleteModal(false);
            } else {
                setActionError(response.message || "Erreur lors de la suppression");
            }
        } catch (err) {
            console.error('Erreur lors de la suppression:', err);
            setActionError("Une erreur est survenue lors de la suppression");
        } finally {
            setProcessingAction(false);
        }
    };

    /**
     * Gérer le changement de page
     */
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    /**
     * Gérer le changement du nombre de lignes par page
     */
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    /**
     * Appliquer les filtres
     */
    const handleApplyFilter = () => {
        setPage(0);
        loadUserListings();
    };

    /**
     * Réinitialiser les filtres
     */
    const handleResetFilter = () => {
        setFilterBy('');
        setFilterValue('');
        setPage(0);
    };

    /**
     * Mettre à jour les paramètres de recherche avancée
     */
    const handleAdvancedSearchChange = (field, value) => {
        setAdvancedSearch(prev => ({
            ...prev,
            [field]: value
        }));
    };

    /**
     * Basculer entre mode simple et avancé
     */
    const handleToggleSearchMode = () => {
        const newMode = searchMode === 'simple' ? 'advanced' : 'simple';
        setSearchMode(newMode);
        setPage(0);
        
        // Réinitialiser les filtres selon le mode
        if (newMode === 'simple') {
            setAdvancedSearch({
                userName: '',
                userEmail: '',
                listingTitle: '',
                listingStatus: '',
                dateFrom: null,
                dateTo: null
            });
        } else {
            setFilterBy('');
            setFilterValue('');
        }
    };

    /**
     * Réinitialiser la recherche avancée
     */
    const handleResetAdvancedSearch = () => {
        setAdvancedSearch({
            userName: '',
            userEmail: '',
            listingTitle: '',
            listingStatus: '',
            dateFrom: null,
            dateTo: null
        });
        setPage(0);
    };

    /**
     * Lancer la recherche avancée manuellement
     */
    const handleAdvancedSearchSubmit = () => {
        setPage(0);
        loadUserListings();
    };

    /**
     * Exporter les résultats
     */
    const handleExport = async (format) => {
        try {
            let exportParams;
            
            if (searchMode === 'advanced') {
                exportParams = {
                    format,
                    filters: {
                        ...advancedSearch,
                        dateFrom: advancedSearch.dateFrom ? advancedSearch.dateFrom.format('YYYY-MM-DD') : null,
                        dateTo: advancedSearch.dateTo ? advancedSearch.dateTo.format('YYYY-MM-DD') : null
                    }
                };
            } else {
                exportParams = {
                    format,
                    filters: {
                        filterBy,
                        filterValue,
                        sortBy,
                        sortOrder
                    }
                };
            }

            const blob = await userListingService.exportUserListings(exportParams);
            
            // Créer un lien de téléchargement
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `user-listings-${searchMode}.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Erreur lors de l\'export:', err);
            setError('Erreur lors de l\'export');
        }
    };

    /**
     * Formater la date d'affichage
     */
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Obtenir la couleur du chip selon le statut
     */
    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'pending':
                return 'warning';
            case 'rejected':
                return 'error';
            case 'suspended':
                return 'default';
            default:
                return 'primary';
        }
    };

    /**
     * Naviguer vers les détails d'une relation
     */
    const handleViewDetails = (userId, listingId) => {
        navigate(`/admin/user-listings/${userId}/${listingId}`);
    };

    /**
     * Naviguer vers l'édition d'une relation
     */
    const handleEditRelation = (userId, listingId) => {
        navigate(`/admin/user-listings/edit/${userId}/${listingId}`);
    };

    /**
     * Envoyer un email à l'utilisateur
     */
    const handleSendEmail = (userId, listingIds) => {
        navigate(`/admin/user-listings/email?userId=${userId}&listingIds=${listingIds.join(',')}`);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <PageHeader
                    title="Gestion User ↔ Listings"
                    subtitle="Gérez les relations entre utilisateurs et leurs annonces"
                    icon={<ManageIcon />}
                />

                {/* Sélecteur de mode de recherche */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">Mode de recherche :</Typography>
                        <Button
                            variant={searchMode === 'simple' ? 'contained' : 'outlined'}
                            onClick={() => setSearchMode('simple')}
                            size="small"
                        >
                            Filtrage simple
                        </Button>
                        <Button
                            variant={searchMode === 'advanced' ? 'contained' : 'outlined'}
                            onClick={() => setSearchMode('advanced')}
                            size="small"
                        >
                            Recherche avancée
                        </Button>
                        
                        {/* Boutons d'export */}
                        {userListings.length > 0 && (
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    onClick={() => handleExport('csv')}
                                    startIcon={<DownloadIcon />}
                                    variant="outlined"
                                >
                                    Export CSV
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => handleExport('excel')}
                                    startIcon={<DownloadIcon />}
                                    variant="outlined"
                                >
                                    Export Excel
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Interface de filtrage simple */}
                {searchMode === 'simple' && (
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Filtrer par</InputLabel>
                                    <Select
                                        value={filterBy}
                                        label="Filtrer par"
                                        onChange={(e) => setFilterBy(e.target.value)}
                                    >
                                        <MenuItem value="">Aucun filtre</MenuItem>
                                        <MenuItem value="user_name">Nom utilisateur</MenuItem>
                                        <MenuItem value="user_email">Email utilisateur</MenuItem>
                                        <MenuItem value="user_id">ID utilisateur</MenuItem>
                                        <MenuItem value="listing_title">Titre annonce</MenuItem>
                                        <MenuItem value="listing_id">ID annonce</MenuItem>
                                        <MenuItem value="listing_status">Statut annonce</MenuItem>
                                        <MenuItem value="category">Catégorie</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Valeur du filtre"
                                    variant="outlined"
                                    value={filterValue}
                                    onChange={(e) => setFilterValue(e.target.value)}
                                    disabled={!filterBy}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Trier par</InputLabel>
                                    <Select
                                        value={sortBy}
                                        label="Trier par"
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <MenuItem value="created_at">Date création</MenuItem>
                                        <MenuItem value="user_name">Nom utilisateur</MenuItem>
                                        <MenuItem value="listing_title">Titre annonce</MenuItem>
                                        <MenuItem value="listing_status">Statut annonce</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleApplyFilter}
                                        startIcon={<SearchIcon />}
                                    >
                                        Filtrer
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleResetFilter}
                                    >
                                        Réinitialiser
                                    </Button>
                                    <IconButton onClick={loadUserListings} color="primary">
                                        <RefreshIcon />
                                    </IconButton>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                {/* Interface de recherche avancée */}
                {searchMode === 'advanced' && (
                    <Accordion defaultExpanded sx={{ mb: 3 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                                <FilterIcon sx={{ mr: 1 }} />
                                Critères de recherche avancée
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Paper sx={{ p: 3 }}>
                                <Grid container spacing={3}>
                                    {/* Recherche utilisateur */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Nom d'utilisateur"
                                            variant="outlined"
                                            value={advancedSearch.userName}
                                            onChange={(e) => handleAdvancedSearchChange('userName', e.target.value)}
                                            placeholder="Rechercher par nom d'utilisateur..."
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Email utilisateur"
                                            variant="outlined"
                                            value={advancedSearch.userEmail}
                                            onChange={(e) => handleAdvancedSearchChange('userEmail', e.target.value)}
                                            placeholder="Rechercher par email..."
                                        />
                                    </Grid>

                                    {/* Recherche annonce */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Titre d'annonce"
                                            variant="outlined"
                                            value={advancedSearch.listingTitle}
                                            onChange={(e) => handleAdvancedSearchChange('listingTitle', e.target.value)}
                                            placeholder="Rechercher par titre d'annonce..."
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Statut d'annonce</InputLabel>
                                            <Select
                                                value={advancedSearch.listingStatus}
                                                label="Statut d'annonce"
                                                onChange={(e) => handleAdvancedSearchChange('listingStatus', e.target.value)}
                                            >
                                                <MenuItem value="">Tous les statuts</MenuItem>
                                                <MenuItem value="active">Active</MenuItem>
                                                <MenuItem value="pending">En attente</MenuItem>
                                                <MenuItem value="rejected">Rejetée</MenuItem>
                                                <MenuItem value="suspended">Suspendue</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Filtres de date */}
                                    <Grid item xs={12} md={6}>
                                        <DatePicker
                                            label="Date de début"
                                            value={advancedSearch.dateFrom}
                                            onChange={(date) => handleAdvancedSearchChange('dateFrom', date)}
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <DatePicker
                                            label="Date de fin"
                                            value={advancedSearch.dateTo}
                                            onChange={(date) => handleAdvancedSearchChange('dateTo', date)}
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </Grid>

                                    {/* Boutons d'action */}
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="outlined"
                                                onClick={handleResetAdvancedSearch}
                                                startIcon={<ClearIcon />}
                                            >
                                                Réinitialiser
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={handleAdvancedSearchSubmit}
                                                startIcon={<SearchIcon />}
                                                disabled={loading}
                                            >
                                                {loading ? 'Recherche...' : 'Rechercher'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                )}

            {/* Tableau des relations */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                {actionSuccess && (
                    <Alert 
                        severity="success" 
                        sx={{ m: 2 }}
                        onClose={() => setActionSuccess(null)}
                    >
                        {actionSuccess}
                    </Alert>
                )}
                
                {actionError && (
                    <Alert 
                        severity="error" 
                        sx={{ m: 2 }}
                        onClose={() => setActionError(null)}
                    >
                        {actionError}
                    </Alert>
                )}

                {/* Interface d'actions en masse */}
                {selectedItems.length > 0 && (
                    <Box sx={{ m: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="subtitle1">
                                {selectedItems.length} élément(s) sélectionné(s)
                            </Typography>
                            
                            <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Action</InputLabel>
                                <Select
                                    value={selectedAction}
                                    onChange={(e) => setSelectedAction(e.target.value)}
                                    label="Action"
                                    disabled={processingAction}
                                >
                                    <MenuItem value="status_change">Changer statut</MenuItem>
                                    <MenuItem value="send_email">Envoyer email</MenuItem>
                                    <MenuItem value="delete">Supprimer relation</MenuItem>
                                </Select>
                            </FormControl>
                            
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleBulkAction}
                                disabled={processingAction || !selectedAction}
                                startIcon={processingAction ? <CircularProgress size={20} /> : null}
                            >
                                {processingAction ? 'En cours...' : 'Appliquer'}
                            </Button>
                            
                            <Button
                                variant="outlined"
                                onClick={() => setSelectedItems([])}
                                disabled={processingAction}
                            >
                                Annuler sélection
                            </Button>
                        </Box>
                    </Box>
                )}
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Tooltip title="Sélectionner tout">
                                                <Checkbox
                                                    indeterminate={selectedItems.length > 0 && selectedItems.length < userListings.length}
                                                    checked={userListings.length > 0 && selectedItems.length === userListings.length}
                                                    onChange={handleSelectAllItems}
                                                    inputProps={{ 'aria-label': 'select all' }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>Utilisateur</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Annonce</TableCell>
                                        <TableCell>Catégorie</TableCell>
                                        <TableCell>Statut</TableCell>
                                        <TableCell>Prix</TableCell>
                                        <TableCell>Date création</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array.isArray(userListings) && userListings.map((listing) => {
                                        const itemId = listing?.id; // Utiliser directement l'UUID du listing avec vérification
                                        if (!itemId) return null; // Ignorer les éléments sans ID
                                        const isItemSelected = isSelected(itemId);
                                        
                                        return (
                                            <TableRow 
                                                key={itemId}
                                                hover
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                selected={isItemSelected}
                                            >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isItemSelected}
                                                    onChange={(event) => handleSelectItem(event, itemId)}
                                                    inputProps={{ 'aria-labelledby': itemId }}
                                                    onClick={(event) => event.stopPropagation()}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {listing.owner?.name || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{listing.owner?.email || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                    {listing.title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{listing.category?.name || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={listing.status}
                                                    color={getStatusColor(listing.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {listing.price ? `${listing.price}€` : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(listing.created_at)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Voir détails">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => handleViewDetails(listing.owner?.id, listing.id)}
                                                        >
                                                            <ViewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Modifier">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => handleEditRelation(listing.owner?.id, listing.id)}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Envoyer email">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => handleSendEmail(listing.owner?.id, [listing.id])}
                                                        >
                                                            <EmailIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                    {(!Array.isArray(userListings) || userListings.length === 0) && !loading && (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    Aucune relation trouvée
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Lignes par page"
                            labelDisplayedRows={({ from, to, count }) => 
                                `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
                            }
                        />
                    </>
                )}
            </Paper>

            {/* Boutons d'actions rapides */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate('/admin/user-listings/stats')}
                    startIcon={<ManageIcon />}
                >
                    Voir statistiques
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/user-listings/bulk-actions')}
                    startIcon={<ManageIcon />}
                >
                    Actions groupées
                </Button>
            </Box>

            {/* Modal de changement de statut */}
            <Dialog open={statusChangeModal} onClose={() => setStatusChangeModal(false)}>
                <DialogTitle>Changer le statut des éléments sélectionnés</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Vous êtes sur le point de changer le statut de {selectedItems.length} annonce(s).
                        Veuillez sélectionner le nouveau statut :
                    </DialogContentText>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Nouveau statut</InputLabel>
                        <Select
                            value={statusToSet}
                            onChange={(e) => setStatusToSet(e.target.value)}
                            label="Nouveau statut"
                        >
                            <MenuItem value="active">Actif</MenuItem>
                            <MenuItem value="inactive">Inactif</MenuItem>
                            <MenuItem value="pending_verification">En attente de vérification</MenuItem>
                            <MenuItem value="rejected">Rejeté</MenuItem>
                            <MenuItem value="expired">Expiré</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setStatusChangeModal(false)} 
                        disabled={processingAction}
                    >
                        Annuler
                    </Button>
                    <Button 
                        onClick={executeStatusChange} 
                        variant="contained" 
                        color="primary" 
                        disabled={processingAction}
                        startIcon={processingAction ? <CircularProgress size={20} /> : null}
                    >
                        {processingAction ? 'En cours...' : 'Confirmer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal d'envoi d'email */}
            <Dialog
                open={emailModal}
                onClose={() => setEmailModal(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Envoyer un email aux propriétaires</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Vous êtes sur le point d'envoyer un email concernant {selectedItems.length} annonce(s).
                    </DialogContentText>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Type d'email</InputLabel>
                        <Select
                            value={emailType}
                            onChange={(e) => setEmailType(e.target.value)}
                            label="Type d'email"
                        >
                            <MenuItem value="custom">Email personnalisé</MenuItem>
                            <MenuItem value="reminder">Rappel</MenuItem>
                            <MenuItem value="warning">Avertissement</MenuItem>
                            <MenuItem value="info">Information</MenuItem>
                        </Select>
                    </FormControl>
                    
                    <TextField
                        fullWidth
                        label="Sujet"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        margin="normal"
                    />
                    
                    <TextField
                        fullWidth
                        label="Message"
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                        margin="normal"
                        multiline
                        rows={8}
                        placeholder="Saisissez votre message ici..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setEmailModal(false)} 
                        disabled={processingAction}
                    >
                        Annuler
                    </Button>
                    <Button 
                        onClick={executeSendEmail} 
                        variant="contained" 
                        color="primary" 
                        disabled={processingAction || (!emailMessage && emailType === 'custom')}
                        startIcon={processingAction ? <CircularProgress size={20} /> : null}
                    >
                        {processingAction ? 'Envoi en cours...' : 'Envoyer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de confirmation de suppression */}
            <Dialog open={confirmDeleteModal} onClose={() => setConfirmDeleteModal(false)}>
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Voulez-vous vraiment supprimer {selectedItems.length} relation(s) entre utilisateurs et annonces ? 
                        Cette action est irréversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setConfirmDeleteModal(false)} 
                        disabled={processingAction}
                    >
                        Annuler
                    </Button>
                    <Button 
                        onClick={executeDelete} 
                        variant="contained" 
                        color="error" 
                        disabled={processingAction}
                        startIcon={processingAction ? <CircularProgress size={20} /> : null}
                    >
                        {processingAction ? 'Suppression...' : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
        </LocalizationProvider>
    );
};

export default UserListingsPage;
